/** Minimal data hook: loading / ready / error with reload. Preloaded keys render immediately. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSurface } from './context';

export type Resource<T> =
  | { status: 'loading'; data: undefined; error: undefined; reload: () => void }
  | { status: 'ready'; data: T; error: undefined; reload: () => void }
  | { status: 'error'; data: undefined; error: unknown; reload: () => void };

export function useResource<T>(key: string, load: () => Promise<T>): Resource<T> {
  const { preload } = useSurface();
  const has = Object.prototype.hasOwnProperty.call(preload, key);
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; data?: T; error?: unknown }>(
    has ? { status: 'ready', data: preload[key] as T } : { status: 'loading' },
  );
  const [tick, setTick] = useState(0);
  const skip = useRef(has);
  const loader = useRef(load);
  loader.current = load;

  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    let live = true;
    setState((s) => (s.status === 'ready' ? s : { status: 'loading' }));
    loader.current().then(
      (data) => { if (live) setState({ status: 'ready', data }); },
      (error: unknown) => { if (live) setState({ status: 'error', error }); },
    );
    return () => { live = false; };
  }, [key, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { ...state, reload } as Resource<T>;
}

/** Runs a mutation and tracks pending / error for a form. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<R | null>(null);
  const run = useCallback(async (...args: A): Promise<R | null> => {
    setPending(true); setError(null);
    try {
      const r = await fn(...args);
      setResult(r);
      return r;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setPending(false);
    }
  }, [fn]);
  return { run, pending, error, result };
}
